import { ConfigurableFormat } from './configurable';

// --- Measure units ---
export enum MeasureUnit {
  BPM = 'bpm',
  CM = 'cm',
  FL_OZ = 'fl oz',
  FT = 'ft',
  IN = 'in',
  KCAL = 'kcal',

  KG = 'kg',
  G = 'g',
  MG = 'mg',
  KJ = 'kJ',
  KM = 'km',

  KMH = 'km/h',
  L = 'L',

  LB = 'lb',
  M = 'm',

  MI = 'mi',
  MIN_PER_KM = 'min/km',
  MIN_PER_MI = 'min/mi',

  ML = 'ml',
  MPH = 'mph',

  OZ = 'oz',
  PERCENT = '%',
  RPM = 'rpm',
  SECONDS = 'sec',

  W = 'W',
}

export interface UserUnitSettings {
  bodyMassIndex?: string | null; // 'kg' | 'oz';
  weight?: string | null; // 'kg' | 'lb' | 'oz';
  height?: string | null; // 'cm' | 'mm' | 'ft';
  energy?: string | null; // 'kcal' | 'kJ';
  volume?: string | null; // 'l' | 'oz';
  length?: string | null; // 'km' | 'mi';
}

// --- Non-convertible units ---
const NON_CONVERTIBLE_UNITS = new Set<MeasureUnit>([
  MeasureUnit.SECONDS,
  MeasureUnit.PERCENT,
  MeasureUnit.BPM,
  MeasureUnit.W,
  MeasureUnit.RPM,
]);

// simple conversion function
export const conversions: Record<
  MeasureUnit,
  Partial<Record<MeasureUnit, (v: number) => number>>
> = {
  // EVERY UNIT REACHES EVERY OTHER UNIT OF ITS OWN DIMENSION, and that is a
  // stronger rule than symmetry. `KG → G` was never declared at all — while the
  // meal mapper normalizes every mass to grams
  // (`MEAL_QUANTITY_UNIT_MAP['kg'] = 'g'`), so the conversion it needs on every
  // save of a kilogram row threw, `getConvertedValueForDB` swallowed it and
  // handed the number back untouched, and two kilograms of rice were written
  // as two grams. `configurable-value-roundtrip.spec.ts` enumerates the
  // dimensions and fails on any gap.
  [MeasureUnit.KG]: {
    [MeasureUnit.KG]: (v) => v,
    [MeasureUnit.G]: (v) => v * 1000,
    [MeasureUnit.MG]: (v) => v * 1_000_000,
    [MeasureUnit.LB]: (v) => v * 2.20462,
    [MeasureUnit.OZ]: (v) => v * 35.274,
  },
  [MeasureUnit.G]: {
    [MeasureUnit.G]: (v) => v,
    [MeasureUnit.KG]: (v) => v / 1000,
    [MeasureUnit.MG]: (v) => v * 1000,
    [MeasureUnit.LB]: (v) => v * 0.00220462,
    [MeasureUnit.OZ]: (v) => v * 0.035274,
  },
  [MeasureUnit.MG]: {
    [MeasureUnit.MG]: (v) => v,
    [MeasureUnit.G]: (v) => v / 1000,
    [MeasureUnit.KG]: (v) => v / 1_000_000,
    [MeasureUnit.LB]: (v) => v / 453_592,
    [MeasureUnit.OZ]: (v) => v / 28350,
  },
  [MeasureUnit.LB]: {
    [MeasureUnit.LB]: (v) => v,
    [MeasureUnit.G]: (v) => v * 453.592,
    [MeasureUnit.MG]: (v) => v * 453_592,
    [MeasureUnit.KG]: (v) => v / 2.20462,
    [MeasureUnit.OZ]: (v) => v * 16,
  },
  [MeasureUnit.OZ]: {
    [MeasureUnit.OZ]: (v) => v,
    [MeasureUnit.KG]: (v) => v / 35.274,
    [MeasureUnit.G]: (v) => v * 28.3495,
    [MeasureUnit.MG]: (v) => v * 28350,
    [MeasureUnit.LB]: (v) => v / 16,
  },
  [MeasureUnit.KM]: {
    [MeasureUnit.KM]: (v) => v,
    [MeasureUnit.M]: (v) => v * 1000,
    [MeasureUnit.CM]: (v) => v * 100_000,
    [MeasureUnit.IN]: (v) => (v * 100_000) / 2.54,
    [MeasureUnit.MI]: (v) => v / 1.60934,
    [MeasureUnit.FT]: (v) => v * 3280.84,
  },
  [MeasureUnit.M]: {
    [MeasureUnit.M]: (v) => v,
    [MeasureUnit.CM]: (v) => v * 100,
    [MeasureUnit.IN]: (v) => (v * 100) / 2.54,
    [MeasureUnit.KM]: (v) => v / 1000,
    [MeasureUnit.MI]: (v) => v / 1609.34,
    [MeasureUnit.FT]: (v) => v * 3.28084,
  },
  [MeasureUnit.MI]: {
    [MeasureUnit.MI]: (v) => v,
    [MeasureUnit.KM]: (v) => v * 1.60934,
    [MeasureUnit.M]: (v) => v * 1609.34,
    [MeasureUnit.CM]: (v) => v * 160_934.4,
    [MeasureUnit.IN]: (v) => v * 63_360,
    [MeasureUnit.FT]: (v) => v * 5280,
  },
  [MeasureUnit.CM]: {
    [MeasureUnit.CM]: (v) => v,
    [MeasureUnit.M]: (v) => v / 100,
    [MeasureUnit.KM]: (v) => v / 100_000,
    [MeasureUnit.MI]: (v) => v / 160_934.4,
    [MeasureUnit.IN]: (v) => v / 2.54,
    [MeasureUnit.FT]: (v) => v / 30.48,
  },
  [MeasureUnit.IN]: {
    [MeasureUnit.IN]: (v) => v,
    [MeasureUnit.CM]: (v) => v * 2.54,
    [MeasureUnit.M]: (v) => (v * 2.54) / 100,
    [MeasureUnit.KM]: (v) => (v * 2.54) / 100_000,
    [MeasureUnit.MI]: (v) => v / 63_360,
    [MeasureUnit.FT]: (v) => v / 12,
  },
  // THE RETURN TRIPS ARE NOT OPTIONAL. `getConvertedValueForUi` converts on
  // load and `getConvertedValueForDB` converts on save, and both SWALLOW a
  // missing entry — `convert` throws, the catch hands the value back untouched,
  // and the untouched value is written into a column that means something else.
  // `cm → ft` existed without `ft → cm`, so a length configurable stored as
  // 182.9 cm was shown to an imperial reader as 6 ft and saved back as SIX
  // CENTIMETRES, under a green save with no error anywhere.
  // `configurable-value-roundtrip.spec.ts` fails on any one-way pair.
  [MeasureUnit.FT]: {
    [MeasureUnit.FT]: (v) => v,
    [MeasureUnit.CM]: (v) => v * 30.48,
    [MeasureUnit.IN]: (v) => v * 12,
    [MeasureUnit.M]: (v) => v / 3.28084,
    [MeasureUnit.MI]: (v) => v / 5280,
    [MeasureUnit.KM]: (v) => v / 3280.84,
  },
  [MeasureUnit.L]: {
    [MeasureUnit.L]: (v) => v,
    [MeasureUnit.ML]: (v) => v * 1000,
    [MeasureUnit.FL_OZ]: (v) => v * 33.814,
  },
  [MeasureUnit.ML]: {
    [MeasureUnit.ML]: (v) => v,
    [MeasureUnit.L]: (v) => v / 1000,
    [MeasureUnit.FL_OZ]: (v) => v / 29.5735,
  },
  [MeasureUnit.FL_OZ]: {
    [MeasureUnit.FL_OZ]: (v) => v,
    [MeasureUnit.L]: (v) => v / 33.814,
    [MeasureUnit.ML]: (v) => v * 29.5735,
  },
  [MeasureUnit.KCAL]: {
    [MeasureUnit.KCAL]: (v) => v,
    [MeasureUnit.KJ]: (v) => v * 4.184,
  },
  [MeasureUnit.KJ]: {
    [MeasureUnit.KJ]: (v) => v,
    [MeasureUnit.KCAL]: (v) => v / 4.184,
  },
  [MeasureUnit.KMH]: {
    [MeasureUnit.KMH]: (v) => v,
    [MeasureUnit.MPH]: (v) => v / 1.60934,
    // `[MeasureUnit.M]: (v) => v / 3.6` used to sit here. It is the km/h → m/s
    // factor written against `M`, which in this enum is METRES — a speed
    // converted into a length. Nothing selects it (`USER_PREFERENCE_UNIT_MAP`
    // sends KMH to KMH or MPH, and `DB_UNIT_MAP` keeps it in KMH), so removing
    // it changes no reachable path; leaving it left a one-way arrow into the
    // wrong dimension for the next caller to find.
  },
  [MeasureUnit.MPH]: {
    [MeasureUnit.MPH]: (v) => v,
    [MeasureUnit.KMH]: (v) => v * 1.60934,
  },
  [MeasureUnit.MIN_PER_KM]: {
    [MeasureUnit.MIN_PER_KM]: (v) => v,
    [MeasureUnit.MIN_PER_MI]: (v) => v * 1.60934,
  },
  [MeasureUnit.MIN_PER_MI]: {
    [MeasureUnit.MIN_PER_MI]: (v) => v,
    [MeasureUnit.MIN_PER_KM]: (v) => v / 1.60934,
  },
  [MeasureUnit.SECONDS]: { [MeasureUnit.SECONDS]: (v) => v },
  [MeasureUnit.BPM]: { [MeasureUnit.BPM]: (v) => v },
  [MeasureUnit.PERCENT]: { [MeasureUnit.PERCENT]: (v) => v },
  [MeasureUnit.W]: { [MeasureUnit.W]: (v) => v },
  [MeasureUnit.RPM]: { [MeasureUnit.RPM]: (v) => v },
};

// --- User preference mapping ---
// 'lb' is the pounds a person weighs themself in; 'oz' is the older imperial
// choice. Small masses (grams of food) are ounces for either.
const isImperialWeight = (u: UserUnitSettings) =>
  u.weight === 'oz' || u.weight === 'lb';
const bodyMassUnit = (u: UserUnitSettings) => {
  if (u.weight === 'lb') return MeasureUnit.LB;
  return u.weight === 'oz' ? MeasureUnit.OZ : MeasureUnit.KG;
};

const USER_PREFERENCE_UNIT_MAP: Record<
  MeasureUnit,
  (userSettings: UserUnitSettings) => MeasureUnit
> = {
  [MeasureUnit.KG]: (u) => bodyMassUnit(u),
  [MeasureUnit.OZ]: (u) => bodyMassUnit(u),
  [MeasureUnit.LB]: (u) => bodyMassUnit(u),
  [MeasureUnit.G]: (u) =>
    isImperialWeight(u) ? MeasureUnit.OZ : MeasureUnit.G,
  [MeasureUnit.MG]: (u) =>
    isImperialWeight(u) ? MeasureUnit.OZ : MeasureUnit.MG,

  [MeasureUnit.CM]: (u) =>
    u.height === 'ft' ? MeasureUnit.FT : MeasureUnit.CM,
  [MeasureUnit.M]: (u) => (u.height === 'ft' ? MeasureUnit.FT : MeasureUnit.CM),
  [MeasureUnit.IN]: (u) =>
    u.height === 'ft' ? MeasureUnit.FT : MeasureUnit.CM,
  [MeasureUnit.FT]: (u) =>
    u.height === 'ft' ? MeasureUnit.FT : MeasureUnit.CM,

  [MeasureUnit.KCAL]: (u) =>
    u.energy === 'kj' ? MeasureUnit.KJ : MeasureUnit.KCAL,
  [MeasureUnit.KJ]: (u) =>
    u.energy === 'kj' ? MeasureUnit.KJ : MeasureUnit.KCAL,

  [MeasureUnit.L]: (u) =>
    u.volume === 'oz' ? MeasureUnit.FL_OZ : MeasureUnit.L,
  [MeasureUnit.ML]: (u) =>
    u.volume === 'oz' ? MeasureUnit.FL_OZ : MeasureUnit.L,
  [MeasureUnit.FL_OZ]: (u) =>
    u.volume === 'oz' ? MeasureUnit.FL_OZ : MeasureUnit.L,

  [MeasureUnit.KM]: (u) =>
    u.length === 'mi' ? MeasureUnit.MI : MeasureUnit.KM,
  [MeasureUnit.MI]: (u) =>
    u.length === 'mi' ? MeasureUnit.MI : MeasureUnit.KM,
  [MeasureUnit.KMH]: (u) =>
    u.length === 'mi' ? MeasureUnit.MPH : MeasureUnit.KMH,
  [MeasureUnit.MPH]: (u) =>
    u.length === 'mi' ? MeasureUnit.MPH : MeasureUnit.KMH,

  [MeasureUnit.MIN_PER_KM]: (u) =>
    u.length === 'mi' ? MeasureUnit.MIN_PER_MI : MeasureUnit.MIN_PER_KM,
  [MeasureUnit.MIN_PER_MI]: (u) =>
    u.length === 'mi' ? MeasureUnit.MIN_PER_MI : MeasureUnit.MIN_PER_KM,

  [MeasureUnit.BPM]: () => MeasureUnit.BPM,
  [MeasureUnit.PERCENT]: () => MeasureUnit.PERCENT,
  [MeasureUnit.RPM]: () => MeasureUnit.RPM,
  [MeasureUnit.SECONDS]: () => MeasureUnit.SECONDS,
  [MeasureUnit.W]: () => MeasureUnit.W,
};

// --- DB preference mapping ---
const DB_UNIT_MAP: Record<MeasureUnit, MeasureUnit> = {
  [MeasureUnit.KG]: MeasureUnit.KG,
  [MeasureUnit.OZ]: MeasureUnit.G,
  [MeasureUnit.LB]: MeasureUnit.KG,
  [MeasureUnit.G]: MeasureUnit.G,
  [MeasureUnit.MG]: MeasureUnit.MG,

  [MeasureUnit.CM]: MeasureUnit.CM,
  [MeasureUnit.M]: MeasureUnit.CM,
  [MeasureUnit.IN]: MeasureUnit.CM,
  [MeasureUnit.FT]: MeasureUnit.CM,

  [MeasureUnit.KCAL]: MeasureUnit.KCAL,
  [MeasureUnit.KJ]: MeasureUnit.KCAL,

  [MeasureUnit.L]: MeasureUnit.L,
  [MeasureUnit.ML]: MeasureUnit.L,
  [MeasureUnit.FL_OZ]: MeasureUnit.L,

  [MeasureUnit.KM]: MeasureUnit.KM,
  [MeasureUnit.MI]: MeasureUnit.KM,
  [MeasureUnit.KMH]: MeasureUnit.KMH,
  [MeasureUnit.MPH]: MeasureUnit.KMH,

  [MeasureUnit.MIN_PER_KM]: MeasureUnit.MIN_PER_KM,
  [MeasureUnit.MIN_PER_MI]: MeasureUnit.MIN_PER_KM,

  [MeasureUnit.BPM]: MeasureUnit.BPM,
  [MeasureUnit.PERCENT]: MeasureUnit.PERCENT,
  [MeasureUnit.RPM]: MeasureUnit.RPM,
  [MeasureUnit.SECONDS]: MeasureUnit.SECONDS,
  [MeasureUnit.W]: MeasureUnit.W,
};

// --- Helpers ---
function isConvertibleUnit(unit?: MeasureUnit): unit is MeasureUnit {
  return !!unit && !NON_CONVERTIBLE_UNITS.has(unit);
}

function getDecimalsBasedOnConfigurableFormat(format?: ConfigurableFormat) {
  switch (format) {
    case ConfigurableFormat.NUMBER:
      return 0;
    case ConfigurableFormat.DECIMAL1:
      return 1;
    case ConfigurableFormat.DECIMAL2:
      return 2;
    case ConfigurableFormat.DECIMAL3:
      return 3;
    default:
      return 0;
  }
}
export function convert(
  value: number,
  from: MeasureUnit,
  to: MeasureUnit,
): number {
  if (from === to) return value;

  const fromMap = conversions[from];
  if (!fromMap) throw new Error(`No conversions defined for ${from}`);

  const converter = fromMap[to];
  if (!converter) throw new Error(`Cannot convert from ${from} to ${to}`);

  return converter(value);
}

/**
 * Whether `convert` can answer this pair — i.e. whether the two units name the
 * same DIMENSION.
 *
 * Exists because the alternative is asking `convert` and catching, and both
 * value helpers below do exactly that and FAIL OPEN: the catch hands the number
 * back untouched, which is only harmless while the caller then stores it under
 * the unit it was typed in. A caller that picks its target unit independently
 * — the meal and grocery mappers do — turns that catch into silent corruption:
 * `3 pcs` written as `3 g`. Asking FIRST lets such a caller choose a target it
 * can actually reach.
 *
 * `from === to` is convertible by definition; `convert` short-circuits it.
 */
export const canConvertUnits = (from: MeasureUnit, to: MeasureUnit): boolean =>
  from === to || Boolean(conversions[from]?.[to]);

// --- UI functions ---
export const getPreferredUnitForUi = (
  unit?: MeasureUnit,
  userSettings?: UserUnitSettings | null,
) => {
  if (!unit || !userSettings || NON_CONVERTIBLE_UNITS.has(unit)) return unit;
  const mapper = USER_PREFERENCE_UNIT_MAP[unit];
  return mapper ? mapper(userSettings) : unit;
};
/**
 * LOAD → SAVE OF AN UNTOUCHED VALUE MUST BE THE IDENTITY — the backend
 * decides whether a save changed the workout's structure (and with it whether
 * clients' in-flight sessions are cancelled) by comparing the echoed value
 * against the stored one, so anything these two functions cannot faithfully
 * round-trip has to pass through UNTOUCHED.
 *
 * Blank is not zero: the GET-side mandatory-configurable fill sends `null`
 * for slots that have no row, and `Number(null)`/`Number('')` are both `0` —
 * an echo that coined a value out of nothing. And a RANGE-format value
 * ('80-130') is not a number at all: `Number()` made it NaN, every save
 * marked the slot changed, and the row was overwritten with the string
 * 'NaN'.
 */
const isBlankConfigurableValue = (
  value: null | number | string | undefined,
): value is '' | null | undefined =>
  value == null || String(value).trim() === '';

// --- UI conversion ---
export const getConvertedValueForUi = ({
  value,
  fromUnit,
  toUnit,
  format,
}: {
  value: null | number | string | undefined;
  fromUnit?: MeasureUnit;
  toUnit?: MeasureUnit;
  format?: ConfigurableFormat;
}) => {
  if (isBlankConfigurableValue(value)) return '';
  if (!fromUnit || !toUnit) {
    return String(value);
  }
  if (!Number.isFinite(Number(value))) return String(value);
  let val = Number(value);
  if (fromUnit === toUnit) return val;

  try {
    val = convert(val, fromUnit, toUnit);
  } catch {
    return Number(value);
  }
  const decimals = getDecimalsBasedOnConfigurableFormat(format);
  return Number(val.toFixed(decimals));
};

// --- DB conversion ---
export const getConvertedValueForDB = ({
  uiConfigurableUnit,
  value,
  configurableFormat,
  dbUnit,
}: {
  uiConfigurableUnit?: MeasureUnit;
  value: null | number | string | undefined;
  configurableFormat?: ConfigurableFormat;
  /**
   * The unit the value was LOADED from — a row edited under a unit
   * preference must return to its own unit, not to a per-dimension default.
   */
  dbUnit?: MeasureUnit;
}) => {
  // Blank stays blank and a non-numeric value (RANGE '80-130') passes
  // through untouched — see `isBlankConfigurableValue`.
  if (isBlankConfigurableValue(value)) return '';
  // If configurable does not have unit or the unit is not convertable, return as-is
  if (!uiConfigurableUnit || !isConvertibleUnit(uiConfigurableUnit))
    return String(value);
  if (!Number.isFinite(Number(value))) return String(value);

  // The unit the value must land in: its OWN (`dbUnit`, carried through the
  // load), falling back to the per-dimension default for callers that never
  // converted on load (meal/grocery mappers). The fallback is what turned a
  // KG row edited under an oz preference into GRAMS — a thousandfold
  // corruption — so any path that converts on load must pass `dbUnit`.
  const preferredUnit =
    dbUnit ?? DB_UNIT_MAP[uiConfigurableUnit] ?? uiConfigurableUnit;
  // If the unit we need in db is same as ui unit, return as is.
  if (preferredUnit === uiConfigurableUnit) return String(value);

  // Lastly, we know we have to convert:
  let converted = Number(value);
  try {
    converted = convert(Number(value), uiConfigurableUnit, preferredUnit);
  } catch {
    return String(value);
  }

  const decimals = getDecimalsBasedOnConfigurableFormat(configurableFormat);
  return converted.toFixed(decimals);
};
